import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Wallet, ArrowUpRight, ArrowDownLeft, KeyRound, Send, Landmark } from "lucide-react";
import { formatINR } from "../lib/format";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Custodian-only capital sub-account panel — shows running balance, ledger
 * entries and payout requests.  Commission-routing entries land here every
 * time a sponsored order is paid + approved.
 */
export const CustodianLedger = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [payoutOpen, setPayoutOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = () => axios.get(`${API}/custodian/capital`)
    .then((r) => setData(r.data))
    .catch(() => setData(null))
    .finally(() => setLoading(false));

  useEffect(() => {
    if (user?.buyer_category !== "custodian") return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  if (!user || user.buyer_category !== "custodian") return null;
  if (loading) return null;
  if (!data) return null;

  const bank = user?.profile?.bank_account || {};
  const entries = data.entries || [];
  const payouts = data.payouts || [];
  const balance = data.account?.balance || 0;

  const submitPayout = async (e) => {
    e.preventDefault();
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { toast.error("Enter a valid payout amount"); return; }
    if (amt > balance) { toast.error("Amount exceeds available balance"); return; }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/custodian/capital/payout`, {
        amount: amt, note: note.trim() || undefined,
      });
      const p = r.data;
      if (p.status === "failed") {
        toast.error(`Payout failed: ${p.provider_detail || "gateway error"}`);
      } else {
        toast.success((p.provider === "phonepe_payouts" || p.provider === "razorpay_payouts") && p.status !== "not_configured" && p.status !== "failed" ? (p.rail === "phonepe" ? "Payout submitted via PhonePe Payouts" : "Payout submitted via Razorpay (fallback)") : "Payout held for the Vinci finance desk");
      }
      setAmount(""); setNote(""); setPayoutOpen(false);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Payout request failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="custodian-ledger" className="mt-16">
      <h2 className="font-serif text-2xl md:text-3xl mb-4 flex items-center gap-3">
        <KeyRound size={20} className="text-[#A78BFA]" />
        Capital sub-account
      </h2>
      <p className="font-body text-xs text-[#8A8A82] mb-5 max-w-2xl">
        Vinci routes every commission owed to your custodianship through this virtual sub-account first, then settles to your real bank account. Commission entries land automatically the moment a sponsored order is paid + approved.
      </p>

      {/* Balance hero */}
      <div className="border border-[#A78BFA]/25 rounded-sm bg-gradient-to-br from-[#0b0912] via-[#0a0910] to-[#08080b] p-6 md:p-8 relative overflow-hidden" style={{ boxShadow: "inset 0 0 60px rgba(167,139,250,0.08)" }}>
        <div className="absolute inset-x-0 top-0 h-[2px] bg-gradient-to-r from-transparent via-[#A78BFA] to-transparent opacity-60" />
        <div className="grid md:grid-cols-[1fr_auto] gap-6 items-end">
          <div>
            <span className="overline text-[#A78BFA] tracking-[0.28em]">Running balance · INR</span>
            <div className="font-serif text-4xl md:text-5xl mt-2 tabular-nums text-[#F5F5F0]" data-testid="capital-balance">
              {formatINR(balance)}
            </div>
            <div className="mt-3 font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#8A8A82]">
              Sub-account · <span className="text-[#A78BFA]">{data.account?.id?.slice(0, 8) || "—"}</span>
              &nbsp;·&nbsp; provisioned {data.account?.created_at ? new Date(data.account.created_at).toLocaleDateString("en-IN") : "—"}
            </div>
            <button
              type="button"
              onClick={() => setPayoutOpen((v) => !v)}
              data-testid="request-payout-btn"
              disabled={balance <= 0}
              className="mt-4 border border-[#A78BFA]/50 hover:bg-[#A78BFA] hover:text-black text-[#A78BFA] font-grotesk text-[10px] uppercase tracking-[0.24em] px-5 py-2 rounded-sm inline-flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <Send size={12} /> Request payout
            </button>
          </div>
          <div className="border-l border-white/[0.08] md:pl-6">
            <div className="font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#8A8A82] mb-2 flex items-center gap-1.5"><Landmark size={11} />Settlement bank</div>
            {bank.holder_name ? (
              <ul className="font-body text-xs text-[#C9C9C2] space-y-1">
                <li>{bank.holder_name}</li>
                <li>{bank.bank_name}</li>
                <li className="text-[#8A8A82]">{bank.ifsc} · ****{(bank.account_number || "").slice(-4)}</li>
              </ul>
            ) : <div className="font-body text-xs text-[#8A8A82]">Not on file yet.</div>}
          </div>
        </div>
      </div>

      {/* Payout form drawer */}
      {payoutOpen && (
        <form onSubmit={submitPayout} data-testid="payout-form" className="mt-4 border border-[#A78BFA]/30 rounded-sm bg-[#0a0912] p-5 grid md:grid-cols-[1fr_2fr_auto] gap-3 items-end">
          <div>
            <label className="font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#8A8A82] block mb-1">Amount (INR) *</label>
            <input data-testid="payout-amount" type="number" required min="1" step="0.01" max={balance} value={amount}
                   onChange={(e) => setAmount(e.target.value)}
                   className="w-full bg-black/40 border border-white/15 rounded-sm px-3 py-2 font-body text-sm text-[#F5F5F0] focus:outline-none focus:border-[#A78BFA]" />
          </div>
          <div>
            <label className="font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#8A8A82] block mb-1">Note (optional)</label>
            <input data-testid="payout-note" value={note} onChange={(e) => setNote(e.target.value)}
                   className="w-full bg-black/40 border border-white/15 rounded-sm px-3 py-2 font-body text-sm text-[#F5F5F0] focus:outline-none focus:border-[#A78BFA]" />
          </div>
          <button data-testid="payout-submit" type="submit" disabled={submitting}
                  className="bg-[#A78BFA] hover:bg-[#8b6fe4] text-black font-grotesk text-[10px] uppercase tracking-[0.24em] px-5 py-2.5 rounded-sm disabled:opacity-50">
            {submitting ? "Submitting…" : "Confirm payout"}
          </button>
        </form>
      )}

      {/* Ledger */}
      <div className="mt-5 border border-white/10 rounded-sm bg-[#0a0a0a] overflow-hidden">
        <div className="px-5 py-3 border-b border-white/[0.06] flex items-center justify-between">
          <span className="font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#8A8A82]">Ledger · newest first</span>
          <span className="font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#B87333]">{entries.length} entries</span>
        </div>
        {entries.length === 0 ? (
          <div className="p-10 text-center">
            <Wallet size={22} className="text-[#8A8A82] mx-auto mb-3" />
            <p className="font-body text-sm text-[#C9C9C2]">No ledger entries yet.</p>
            <p className="font-body text-xs text-[#8A8A82] mt-1 max-w-md mx-auto">
              Entries appear here the moment a sponsored order is paid and approved.
            </p>
          </div>
        ) : (
          <ul>
            {entries.map((e, i) => (
              <li key={e.id || i} data-testid={`ledger-entry-${e.id || i}`} className="px-5 py-3 flex items-center gap-4 border-b border-white/[0.04] last:border-b-0">
                <span className={`grid place-items-center w-8 h-8 rounded-sm shrink-0 ${e.kind === "credit" ? "text-[#2FB999] border border-[#2FB999]/45 bg-[#2FB999]/[0.05]" : "text-[#FF5A5F] border border-[#FF5A5F]/45 bg-[#FF5A5F]/[0.05]"}`}>
                  {e.kind === "credit" ? <ArrowDownLeft size={14} /> : <ArrowUpRight size={14} />}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="font-body text-sm text-[#F5F5F0] truncate">{e.memo || e.kind}</div>
                  <div className="font-grotesk text-[9px] uppercase tracking-[0.2em] text-[#8A8A82] mt-0.5">{new Date(e.created_at).toLocaleString("en-IN")}</div>
                </div>
                <div className={`font-serif text-lg tabular-nums shrink-0 ${e.kind === "credit" ? "text-[#2FB999]" : "text-[#FF5A5F]"}`}>
                  {e.kind === "credit" ? "+" : "−"}{formatINR(Math.abs(e.amount))}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Payout history */}
      {payouts.length > 0 && (
        <div className="mt-5 border border-white/10 rounded-sm bg-[#0a0a0a] overflow-hidden">
          <div className="px-5 py-3 border-b border-white/[0.06] flex items-center justify-between">
            <span className="font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#8A8A82]">Payout requests</span>
            <span className="font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#A78BFA]">{payouts.length}</span>
          </div>
          <ul>
            {payouts.map((p) => {
              const color = p.status === "processed" ? "#2FB999" : p.status === "failed" ? "#FF5A5F" : "#B87333";
              return (
                <li key={p.id} data-testid={`payout-row-${p.id}`} className="px-5 py-3 grid grid-cols-[auto_1fr_auto] items-center gap-3 border-b border-white/[0.04] last:border-b-0">
                  <span className="font-grotesk text-[9px] uppercase tracking-[0.2em] px-2 py-1 rounded-sm border" style={{ color, borderColor: color + "60", background: color + "10" }}>
                    {p.status}
                  </span>
                  <div className="min-w-0">
                    <div className="font-body text-sm text-[#F5F5F0] truncate">{p.memo || "Settlement"}</div>
                    <div className="font-grotesk text-[9px] uppercase tracking-[0.2em] text-[#8A8A82] mt-0.5">
                      {p.provider} · {new Date(p.created_at).toLocaleString("en-IN")}
                      {p.provider_reference ? ` · ref ${p.provider_reference}` : ""}
                    </div>
                  </div>
                  <div className="font-serif text-lg tabular-nums text-[#F5F5F0]">{formatINR(p.amount)}</div>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};
